import { Room, RoomEvent } from 'livekit-client';

export interface AudioUnblockHandlers {
  onPlaybackBlocked: () => void;
  onPlaybackResumed?: () => void;
}

/**
 * Wires the LiveKit autoplay-policy unblock contract on a Room:
 *
 * 1. After `room.connect()` resolves the caller should `await
 *    primeAudioPlayback(room)` while still inside the user-gesture
 *    promise chain — this authorises remote `<audio>` playback.
 * 2. `wireAudioPlaybackWatcher(room, …)` listens for
 *    `RoomEvent.AudioPlaybackStatusChanged` and invokes the
 *    `onPlaybackBlocked` callback whenever the browser later
 *    revokes playback (tab backgrounded mid-handshake, gesture
 *    context lost, etc.) so the UI can surface a re-arm action.
 *
 * Extracted from `call-context.tsx` so the contract is unit-testable
 * without pulling in the full CallProvider dependency graph
 * (TanStack Query, auth, WebSocket, etc.). See task #179.
 */
export async function primeAudioPlayback(room: Pick<Room, 'startAudio'>): Promise<void> {
  try {
    await room.startAudio();
  } catch (err) {
    console.warn('[Call/audio] startAudio after connect failed', err);
  }
}

export function wireAudioPlaybackWatcher(
  room: Pick<Room, 'on' | 'canPlaybackAudio'>,
  handlers: AudioUnblockHandlers,
): void {
  room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
    const canPlay = room.canPlaybackAudio;
    console.info('[Call/audio] audioPlaybackStatusChanged', { canPlay });
    if (canPlay) {
      handlers.onPlaybackResumed?.();
      return;
    }
    handlers.onPlaybackBlocked();
  });
  // Close the missed-initial-state edge case: if playback was already
  // blocked before we attached the listener (e.g. the autoplay refusal
  // fired during the connect handshake, before this code ran), no
  // future event will be emitted until state changes. Evaluate once,
  // immediately, so the recovery toast still appears.
  if (!room.canPlaybackAudio) {
    console.info('[Call/audio] audio already blocked at watcher attach');
    handlers.onPlaybackBlocked();
  }
}
